import {
  Button,
  CheckboxField,
  Fieldset,
  Grid,
  SelectField,
  TextField,
} from "@aws-amplify/ui-react";
import React, { useState } from "react";
import { Schema } from "../../amplify/data/resource";
import { RoutinePattern, RhythmPart, HoldPart } from "../models/RoutinePattern";
import { I18n } from "aws-amplify/utils";

type Pattern = Schema["Pattern"]["type"];
type Variation = Schema["Variation"]["type"];

interface RoutinePatternFormProps {
  patterns: Pattern[];
  variations: Map<string, Variation[]>;
  nextOrder: number;
  saveRoutinePattern: (routinePattern: RoutinePattern) => void;
}

const RoutinePatternForm: React.FC<RoutinePatternFormProps> = ({
  patterns,
  variations,
  nextOrder,
  saveRoutinePattern,
}) => {
  const [step, setStep] = useState(0);
  const [selectedPattern, setSelectedPattern] = useState("");
  const [selectedVariation, setSelectedVariation] = useState("");
  const [rhythm, setRhythm] = useState<RhythmPart[]>([]);
  const [hold, setHold] = useState<HoldPart[]>([]);

  const [order, setOrder] = useState(0);
  const [error, setError] = useState(false);

  const [rhythmList, setRhythmList] = useState<string[]>([]);
  const [holdList, setHoldList] = useState<string[]>([]);

  const nextStep = () => setStep(step + 1);
  const prevStep = () => setStep(step - 1);

  const handlePatternSelect = (pattern: string) => setSelectedPattern(pattern);
  const handleVariationSelect = (variation: string) =>
    setSelectedVariation(variation);
  const handleOrderChange = (newOrder: string) => setOrder(parseInt(newOrder));

  const [variationsList, setVariationsList] = useState<Variation[]>([]);

  React.useEffect(() => {
    if (selectedPattern) {
      const variationsList = variations.get(selectedPattern) || [];
      setVariationsList(variationsList);
    }
  }, [selectedPattern, variations]);

  React.useEffect(() => {
    if (selectedVariation) {
      const variation = variationsList.find(
        (variation) => variation.id === selectedVariation
      );
      if (variation) {
        // S(QQS) -> [S, (QQS)]
        const splitResultRhythm = variation.rhytm
          .split(/[(|)]/)
          .filter((a) => a.trim() !== "");
        // create string[] from split results
        const rhythms = splitResultRhythm || [];
        const rhythm = rhythms.map((rhythm) => {
          return { use: false, rhythm };
        });
        setRhythmList(rhythms);
        setRhythm(rhythm);
        // split hold arrays of strings using regex but discard the delimiters
        // N-P -> [N, P]
        const splitResultHolds = variation.hold.split(/[-]/);
        const holds = splitResultHolds || [];
        const hold = holds.map((hold) => {
          return { use: false, hold };
        });
        setHoldList(holds);
        setHold(hold);
      }
    }
  }, [selectedVariation, variationsList]);

  React.useEffect(() => {
    setOrder(nextOrder);
  }, [nextOrder]);

  React.useEffect(() => {
    setError(
      rhythm.filter((value) => value.use === true).length === 0 ||
        hold.filter((value) => value.use === true).length === 0 ||
        order === 0
    );
  }, [rhythm, hold, order]);

  switch (step) {
    case 0:
      return (
        <Grid
          as="form"
          rowGap="15px"
          columnGap="15px"
          padding="20px"
          onSubmit={async (event) => event.preventDefault()}
        >
          <SelectField
            value={selectedPattern}
            onChange={(e) => handlePatternSelect(e.target.value)}
            onBlur={(e) => handlePatternSelect(e.target.value)}
            label={I18n.get("Pattern")}
            isRequired={true}
          >
            <option value="">{I18n.get("Select a pattern")}</option>
            {patterns.map((pattern) => (
              <option key={pattern.id} value={pattern.id}>
                {(pattern.order ?? 0) / 10} - {pattern.name}
              </option>
            ))}
          </SelectField>
          <Button
            variation="primary"
            onClick={nextStep}
            disabled={selectedPattern === ""}
          >
            {I18n.get("Next")}
          </Button>
          <Button onClick={prevStep}>{I18n.get("Previous")}</Button>
        </Grid>
      );
    case 1:
      return (
        <Grid
          as="form"
          rowGap="15px"
          columnGap="15px"
          padding="20px"
          onSubmit={async (event) => event.preventDefault()}
        >
          <SelectField
            value={selectedVariation}
            onChange={(e) => handleVariationSelect(e.target.value)}
            onBlur={(e) => handleVariationSelect(e.target.value)}
            label={I18n.get("Variation")}
            isRequired={true}
          >
            <option value="">{I18n.get("Select a variation")}</option>
            {variationsList.map((variation) => (
              <option key={variation.id} value={variation.id}>
                {(variation.order ?? 0) / 10} - {variation.rhytm} -{" "}
                {variation.hold}
              </option>
            ))}
          </SelectField>
          <Button
            variation="primary"
            onClick={nextStep}
            disabled={selectedVariation === ""}
          >
            {I18n.get("Next")}
          </Button>
          <Button onClick={prevStep}>{I18n.get("Previous")}</Button>
        </Grid>
      );
    case 2:
      return (
        <Grid
          as="form"
          rowGap="15px"
          columnGap="15px"
          padding="20px"
          onSubmit={async (event) => {
            event.preventDefault();

            const _error =
              rhythm.filter((value) => value.use === true).length === 0 ||
              hold.filter((value) => value.use === true).length === 0 ||
              order === 0;

            setError(_error);

            if (_error) return;

            saveRoutinePattern({
              patternId: selectedPattern,
              variationId: selectedVariation,
              rhythm: Array.from(rhythm.values()),
              hold: Array.from(hold.values()),
              order,
            });
          }}
        >
          <Fieldset legend={I18n.get("Rhythm")}>
            {rhythmList.map((rhythmItem, index) => (
              <CheckboxField
                label={rhythmItem}
                key={index}
                value={rhythmItem}
                name={`rhythm-${index}`}
                onChange={(e) => {
                  if (e.target.checked) {
                    rhythm[index] = { use: true, rhythm: e.target.value };
                    setRhythm(rhythm);
                  } else {
                    rhythm[index] = { use: false, rhythm: e.target.value };
                    setRhythm(rhythm);
                  }
                }}
                onBlur={() => {
                  setError(
                    rhythm.filter((value) => value.use === true).length === 0 ||
                      hold.filter((value) => value.use === true).length === 0 ||
                      order === 0
                  );
                }}
              />
            ))}
          </Fieldset>
          <Fieldset legend={I18n.get("Hold")}>
            {holdList.map((holdItem, index) => (
              <CheckboxField
                label={holdItem}
                key={index}
                value={holdItem}
                name={`hold-${index}`}
                onChange={(e) => {
                  if (e.target.checked) {
                    hold[index] = { use: true, hold: e.target.value };
                    setHold(hold);
                  } else {
                    hold[index] = { use: false, hold: e.target.value };
                    setHold(hold);
                  }
                }}
                onBlur={() => {
                  setError(
                    rhythm.filter((value) => value.use === true).length === 0 ||
                      hold.filter((value) => value.use === true).length === 0 ||
                      order === 0
                  );
                }}
              />
            ))}
          </Fieldset>
          <TextField
            label={I18n.get("Order")}
            value={order}
            onChange={(e) => handleOrderChange(e.target.value)}
          />
          <Button variation="primary" type="submit" disabled={error}>
            {I18n.get("Add")}
          </Button>
          <Button onClick={prevStep}>{I18n.get("Previous")}</Button>
        </Grid>
      );
    default:
      return null;
  }
};

export default RoutinePatternForm;
